import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import api from "../services/axios";

const getQueryParamsFromFilteredData = (
  filteredData,
  locationPoint,
  selectedCity,
  userBaseCity,
  isNearbySearch = false,
  locationData,
  trainStation
) => {
  const queryParams = new URLSearchParams();
  queryParams.append("view", "clubs");

  const filterLocality = filteredData?.locality
    ?.filter((item) => item.checked)
    .map((item) => item.value)
    ?.join("|");

  // Only send locality when the user explicitly selected it in filters.
  // GPS/train-station locality would hide nearby clubs from other areas.
  const queryLocality = filterLocality || null;

  const musicGenre = filteredData?.musicGenre
    ?.filter((item) => item.checked)
    .map((item) => item.value)
    ?.join("|");

  if (isNearbySearch) {
    if (
      locationPoint &&
      Number.isFinite(locationPoint?.coordinates?.[0]) &&
      Number.isFinite(locationPoint?.coordinates?.[1])
    ) {
      queryParams.append("location", JSON.stringify(locationPoint));
      queryParams.append("coordinates", locationPoint.coordinates.join(","));
    }
  }

  if (queryLocality) queryParams.append("locality", queryLocality);
  // Don't override an explicit locality filter with train-station params.
  if (trainStation && !filterLocality) {
    queryParams.append("trainStation", trainStation.name);
  }
  if (musicGenre) queryParams.append("musicGenre", musicGenre);

  const directMappings = {
    vegNonVeg: "vegNonVeg",
    stagsAllowed: "stagsAllowed",
    kidsFriendly: "kidsFriendly",
    happyHours: "happyHoursTimings",
    sheesha: "seeshaServe",
    liveMusicDj: "liveMusicDj",
  };

  for (const key in directMappings) {
    if (filteredData[key]) {
      queryParams.append(directMappings[key], filteredData[key]);
    }
  }

  if (selectedCity) queryParams.append("city", selectedCity);
  if (userBaseCity) queryParams.append("userBaseCity", userBaseCity);

  return queryParams.toString();
};

export const useFetchClubs = (
  filteredData,
  locationPoint,
  selectedCity,
  userBaseCity,
  isNearbySearch = false,
  enabled = true,
  locationData,
  trainStation
) => {
  const fetchClubsPage = async ({
    pageParam = 0,
    includeLocation = false,
    includeTrainStation = true,
  }) => {
    const queryParams = getQueryParamsFromFilteredData(
      filteredData,
      locationPoint,
      selectedCity,
      userBaseCity,
      includeLocation ? isNearbySearch : false,
      includeLocation ? locationData : null,
      includeTrainStation ? trainStation : null
    );
    const token = localStorage.getItem("userToken");
    const jsonToken = JSON.parse(token);
    const response = await api.get(`api/clubs?page=${pageParam}&${queryParams}`, {
      headers: {
        Authorization: jsonToken ? `Bearer ${jsonToken}` : null,
      },
    });

    if (response.data?.ok === false) {
      throw new Error(response.data?.message || "Something went wrong");
    }

    return response.data;
  };

  return useInfiniteQuery({
    queryKey: [
      "api/clubs",
      {
        filteredData,
        locationPoint: Array.isArray(locationPoint?.coordinates)
          ? locationPoint.coordinates.map((value) => Number(Number(value).toFixed(4)))
          : null,
        selectedCity,
        userBaseCity,
        isNearbySearch,
        trainStation: trainStation?.name ?? null,
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const locationAwareResponse = await fetchClubsPage({
        pageParam,
        includeLocation: true,
      });

      const clubs = Array.isArray(locationAwareResponse?.data)
        ? locationAwareResponse.data
        : [];

      if (isNearbySearch && clubs.length === 0) {
        return fetchClubsPage({
          pageParam,
          includeLocation: false,
          includeTrainStation: false,
        });
      }

      return locationAwareResponse;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.cursor ?? undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled:
      enabled &&
      (!!selectedCity ||
        (isNearbySearch &&
          locationPoint &&
          Number.isFinite(locationPoint?.coordinates?.[0]) &&
          Number.isFinite(locationPoint?.coordinates?.[1]))),
  });
};
